import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, REFERRAL_CONSENT, tcpLabel } from '@rydres/shared';
import { Button, Field, Input, Notice, Spinner, TextArea, fmtPhone, formatUsd } from '@rydres/ui';
import { api } from '../lib/api.js';
import { sendRideRequest } from '../lib/passenger.js';
import type { PassengerMe, PublicPage } from '../lib/types.js';
import { Backups } from './Backups.js';
import { Shell, useRiderGate } from './Passenger.js';
import { EMPTY_TRIP, TripFields, toIso, useSlotWarning } from './TripFields.js';

function DriverPhoto({ name, url, size = 'h-14 w-14' }: { name: string; url: string | null; size?: string }) {
  return url ? (
    <img src={url} alt="" className={`${size} shrink-0 rounded-2xl object-cover`} />
  ) : (
    <div className={`flex ${size} shrink-0 items-center justify-center rounded-2xl bg-brand text-xl font-black text-on-brand`}>{name.slice(0, 1)}</div>
  );
}

/** A rider with several drivers says which one this ride is for. */
function ChooseDriver({ me }: { me: PassengerMe }) {
  return (
    <Shell>
      <h1 className="text-2xl font-black">Add Ride Service</h1>
      {me.drivers.length === 0 ? (
        <Notice tone="info">You have no driver yet. Ask your driver for their {BRAND.name} link or QR code; {BRAND.name} does not pick drivers for you.</Notice>
      ) : (
        <>
          <p className="text-sm text-ink2">Which driver is this ride for?</p>
          <ul className="space-y-2">
            {me.drivers.map((d) => (
              <li key={d.handle}>
                <Link to={`/me/request/${d.handle}`} className="flex items-center gap-3 rounded-2xl border border-line bg-card p-3 font-bold">
                  <DriverPhoto name={d.displayName ?? d.handle} url={d.photoUrl} size="h-11 w-11" />
                  {d.displayName ?? d.handle}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Shell>
  );
}

function RequestForm({ me, page, wanted, referrals }: { me: PassengerMe; page: PublicPage; wanted: string | null; referrals: boolean }) {
  const navigate = useNavigate();
  const [serviceId, setServiceId] = useState(page.services.find((s) => s.id === wanted)?.id ?? '');
  const service = page.services.find((s) => s.id === serviceId);
  const canCard = page.acceptedModes.includes('card');
  const canDirect = page.acceptedModes.some((m) => m !== 'card');
  const [payment, setPayment] = useState<'card' | 'direct'>(canCard ? 'card' : 'direct');
  const [trip, setTrip] = useState(EMPTY_TRIP);
  const [name, setName] = useState(me.details.name ?? '');
  const [notes, setNotes] = useState('');
  const [allowReferral, setAllowReferral] = useState(me.allowReferrals);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const warning = useSlotWarning(page, trip.startsAt, service?.durationMin ?? 60);
  const driverName = page.displayName ?? 'your driver';
  // The rider's own places first, then the ones the driver shows every rider.
  const places = [...me.places, ...page.places].filter((p, i, all) => all.findIndex((q) => q.label === p.label && q.address === p.address) === i);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!trip.startsAt) return setError('Pick a pickup date and time.');
    setSending(true);
    try {
      const r = await sendRideRequest({
        handle: page.handle,
        serviceId: service?.id ?? null,
        ...(service ? { payment: canDirect && (payment === 'direct' || !canCard) ? 'direct' : 'card' } : {}),
        riderName: name.trim(),
        passengers: trip.passengers,
        pickupAddress: trip.pickupAddress,
        dropoffAddress: trip.dropoffAddress,
        pickupPoint: trip.pickupPoint,
        dropoffPoint: trip.dropoffPoint,
        startsAt: toIso(trip.startsAt),
        notes: notes.trim() || null,
        ...(referrals ? { allowReferral } : {}),
      });
      navigate(`/b/${r.booking.manageToken}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      setSending(false);
    }
  };

  return (
    <Shell>
      <h1 className="text-2xl font-black">Add Ride Service</h1>
      <div className="flex items-center gap-3">
        <DriverPhoto name={driverName} url={page.photoUrl} />
        <div className="min-w-0">
          <div className="font-bold">{page.displayName}</div>
          <div className="text-sm text-ink2">{page.legalName}</div>
          <div className="text-xs text-muted">{tcpLabel(page.tcpNumber)}</div>
        </div>
      </div>
      {!page.live ? (
        <>
          <Notice tone="warn">{driverName} is not taking requests right now. Call {fmtPhone(page.phone)} instead.</Notice>
          <Backups list={page.backups} driverName={page.displayName} />
        </>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <section className="grid gap-3 sm:grid-cols-2">
            <TripFields
              page={page}
              form={trip}
              onChange={setTrip}
              service={service}
              places={places}
              warning={warning}
              warningExtra={
                <div className="sm:col-span-2">
                  <p className="text-xs text-ink2">You can still ask; {driverName} decides.</p>
                  <Backups list={page.backups} driverName={page.displayName} title={`Or book a driver ${page.displayName ?? 'this driver'} trusts`} />
                </div>
              }
            />
            <Field label="Your name">
              <Input required minLength={2} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Additional instructions (optional)">
                <TextArea maxLength={500} placeholder="Flight number, bags, child seat, anything your driver should know" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </div>
          </section>

          {page.services.length > 0 && (
            <section>
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">Or pick one of {driverName}'s priced services (optional)</h2>
              <div className="space-y-2">
                {page.services.map((s) => (
                  <label key={s.id} className={`flex cursor-pointer items-start justify-between gap-3 rounded-2xl border p-3 ${serviceId === s.id ? 'border-accent bg-accent-soft/40 ring-1 ring-accent' : 'border-line bg-card'}`}>
                    <input type="checkbox" className="sr-only" checked={serviceId === s.id} onChange={() => setServiceId(serviceId === s.id ? '' : s.id)} />
                    <div>
                      <div className="font-bold">{s.name}</div>
                      {s.description && <div className="text-sm text-ink2">{s.description}</div>}
                      <div className="mt-1 text-xs text-muted">Up to {s.maxPassengers} passengers · per vehicle</div>
                    </div>
                    <div className="text-lg font-black">{formatUsd(s.priceCents)}</div>
                  </label>
                ))}
              </div>
            </section>
          )}

          {service && canCard && canDirect && (
            <section>
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">How you'll pay</h2>
              <div className="flex gap-2 rounded-2xl bg-card2 p-1">
                {(['card', 'direct'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setPayment(m)} className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold ${payment === m ? 'bg-card shadow-card' : 'text-muted'}`}>
                    {m === 'card' ? 'Card, once accepted' : 'Pay the driver directly'}
                  </button>
                ))}
              </div>
            </section>
          )}

          {referrals && (
            <label className="flex items-start gap-2 text-sm text-ink2">
              <input type="checkbox" className="mt-1" checked={allowReferral} onChange={(e) => setAllowReferral(e.target.checked)} />
              <span>{REFERRAL_CONSENT}</span>
            </label>
          )}

          <div className="space-y-2 rounded-2xl border border-line bg-card p-4 text-xs text-muted">
            <p className="text-sm text-ink2">Nothing is charged now. A request is not a booking until {driverName} accepts it and you agree to the price.</p>
            <p>{page.cancelPolicyText}</p>
            <p>Your contract is with {page.legalName ?? page.displayName}, a licensed California charter-party carrier ({tcpLabel(page.tcpNumber)}). {BRAND.name} provides the booking software only.</p>
            <p>
              By sending this request you agree to receive text messages about this booking from your driver through {BRAND.name} (confirmation, reminder, changes). Message and data rates may apply. Message frequency varies by booking. Reply STOP to stop, HELP for help. See our <a className="underline" href="/privacy">privacy policy</a> and <a className="underline" href="/terms">terms</a>.
            </p>
          </div>

          {error && <Notice tone="danger">{error}</Notice>}
          <Button type="submit" disabled={sending} className="w-full">{sending ? 'One moment…' : `Send to ${driverName}`}</Button>
        </form>
      )}
    </Shell>
  );
}

/** rydres.com/me/request[/<handle>]: a signed-in rider asks one of their drivers for a ride. */
export function RequestPage() {
  const { handle } = useParams();
  const [params] = useSearchParams();
  const { p, gate } = useRiderGate('Add Ride Service', `Sign in with the mobile number you book with to ask your ${BRAND.name} driver for a ride.`);
  const [page, setPage] = useState<PublicPage | null | undefined>(undefined);
  useEffect(() => {
    if (!handle) return;
    setPage(undefined);
    api.call<PublicPage>('GET', `/public/pages/${encodeURIComponent(handle)}`, { auth: false }).then(setPage, () => setPage(null));
  }, [handle]);
  if (gate) return gate;
  const me = p.me!;
  if (!handle) return me.drivers.length === 1 ? <Navigate to={`/me/request/${me.drivers[0]!.handle}`} replace /> : <ChooseDriver me={me} />;
  if (page === undefined) return <Shell><Spinner /></Shell>;
  if (page === null) return <Shell><Notice tone="warn">There is no driver at this address.</Notice></Shell>;
  return <RequestForm key={page.handle} me={me} page={page} wanted={params.get('service')} referrals={p.referrals} />;
}
